import {
  faEnvelope,
  faEnvelopeCircleCheck,
} from '@fortawesome/pro-light-svg-icons';
import { faEnvelopeDot } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { Modal } from '#src/atoms/Modal/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { CloseButton } from '#src/atoms/buttons/CloseButton/index.tsx';
import { LoadingButton } from '#src/atoms/buttons/LoadingButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineL, LabelS } from '#src/atoms/typography/index.tsx';
import type { GoogleReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { ReCAPTCHA } from '#src/components/recaptcha/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useLogout } from '#src/hooks/auth/useLogOut.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { avoidOrphan } from '#src/lib/text.ts';
import { laptopAndAbove } from '#src/style/mediaQuery.ts';

import { useResendConfirmationInstructions } from './useResendConfirmationInstructions.ts';

const FixedWrapper = styled(Vertical)`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background-color: var(--c-black);
`;

const TopRight = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--double-unit);
  padding: var(--triple-unit) var(--triple-unit) var(--unit) var(--triple-unit);
  text-align: center;
  @media ${laptopAndAbove} {
    width: 480px;
  }
`;
const GreyText = styled(BodyM)`
  color: var(--c-nd-600);
`;
const ButtonContainer = styled(Vertical)`
  padding: var(--unit);
`;
const StyledButtonBase = styled(ButtonBase)`
  text-decoration: underline;
`;

const EnveloppeIcon = styled(FontAwesomeIcon)`
  margin-left: var(--unit);
`;

const buttonTextMessages = defineMessages({
  emailNotSent: {
    defaultMessage: 'Resend an email',
  },
  emailSent: {
    defaultMessage: 'Email sent',
  },
});

type Props = { closable?: boolean; onClose: () => void };

export const EmailVerificationDialog = ({
  closable = true,
  onClose,
}: Props) => {
  const track = useEvents();
  const { formatMessage } = useIntl();
  const resendConfirmationInstructions = useResendConfirmationInstructions();
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { currentUser } = useCurrentUserContext();
  const logOut = useLogout();
  const recaptchaRef = useRef<GoogleReCAPTCHA>(null);

  useEffect(() => {
    track('View Email Confirmation Popin');
  }, [track]);

  useEffect(() => {
    if (currentUser?.confirmed) {
      onClose();
    }
  }, [currentUser?.confirmed, onClose]);

  const onClick = async () => {
    setLoading(true);

    recaptchaRef.current?.reset();
    const recaptchaTokenV2 = await recaptchaRef.current?.executeAsync();

    if (recaptchaTokenV2) {
      const { errors } = await resendConfirmationInstructions({
        variables: {
          input: {
            recaptchaTokenV2,
            fromPath: location.pathname,
          },
        },
      });
      if (!errors.length) {
        setEmailSent(true);
      }
    }
    setLoading(false);
  };
  return (
    <Modal blocking>
      <FixedWrapper center>
        {!!closable && (
          <TopRight>
            <CloseButton onClose={onClose} size="medium" />
          </TopRight>
        )}
        <DialogContainer>
          <FontAwesomeIcon
            icon={faEnvelopeDot}
            fontSize={64}
            color="var(--c-blue-400)"
          />

          <HeadlineL as="h3">
            {avoidOrphan(
              formatMessage({
                defaultMessage: 'We still need you to confirm your account.',
              })
            )}
          </HeadlineL>

          <GreyText>
            <FormattedMessage defaultMessage="Check your emails to confirm your account or try resending a new confirmation email." />
          </GreyText>

          <ReCAPTCHA ref={recaptchaRef} />

          <ButtonContainer gap={4}>
            <LoadingButton
              size="medium"
              color="primary"
              onClick={() => {
                onClick();
              }}
              disabled={emailSent}
              loading={loading}
              startIcon={
                !loading &&
                (emailSent ? (
                  <EnveloppeIcon icon={faEnvelopeCircleCheck} />
                ) : (
                  <EnveloppeIcon icon={faEnvelope} />
                ))
              }
            >
              {formatMessage(
                emailSent
                  ? buttonTextMessages.emailSent
                  : buttonTextMessages.emailNotSent
              )}
            </LoadingButton>

            <LabelS color="var(--c-nd-400)">
              <FormattedMessage
                defaultMessage="Incorrect email address? <b>Logout</b>"
                values={{
                  b: chunks => (
                    <StyledButtonBase onClick={logOut}>
                      {chunks}
                    </StyledButtonBase>
                  ),
                }}
              />
            </LabelS>
          </ButtonContainer>
        </DialogContainer>
      </FixedWrapper>
    </Modal>
  );
};
